/**
 * Where a class went wrong, question by question.
 *
 * Marks say how a class did; this says why. Each question shows how many got
 * it right and wrong, and the wrong answer most students chose, which is
 * usually the misconception worth reteaching. Difficult questions are flagged
 * only from real response counts (see isDifficult).
 */

import { useState } from 'react';
import { AlertTriangle, Check } from 'lucide-react';
import { Badge } from '../ui/badge';
import { formatPercent, isDifficult, type QuestionStat } from '../../lib/analytics/metrics';
import { AnalyticsEmptyState, AnalyticsNote } from './AnalyticsPanel';

const letter = (i: number) => String.fromCharCode(65 + i);

export function QuestionAnalytics({
  questions,
  coverage,
  totalResults,
}: {
  questions: QuestionStat[];
  /** Results that carried per-question data. */
  coverage: number;
  totalResults: number;
}) {
  const [order, setOrder] = useState<'quiz' | 'hardest'>('quiz');

  if (questions.length === 0) {
    return (
      <AnalyticsEmptyState
        compact
        title="No question-level data"
        description={
          totalResults > 0
            ? 'These results were recorded before answers were stored per question.'
            : 'Question analytics appear once students submit the quiz.'
        }
      />
    );
  }

  const sorted =
    order === 'hardest'
      ? [...questions].sort((a, b) => (a.accuracy ?? 101) - (b.accuracy ?? 101))
      : questions;
  const difficult = questions.filter((q) => isDifficult(q)).length;

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          {difficult > 0 ? (
            <>
              <span className="font-medium text-foreground">{difficult}</span> question{difficult === 1 ? '' : 's'} answered correctly by
              fewer than half the class
            </>
          ) : (
            'No question was answered correctly by fewer than half the class.'
          )}
        </p>
        <div className="inline-flex rounded-md border border-border p-0.5 text-xs" role="group" aria-label="Order questions">
          {(['quiz', 'hardest'] as const).map((o) => (
            <button
              key={o}
              type="button"
              aria-pressed={order === o}
              onClick={() => setOrder(o)}
              className={`rounded px-2.5 py-1 ${order === o ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            >
              {o === 'quiz' ? 'Quiz order' : 'Hardest first'}
            </button>
          ))}
        </div>
      </div>

      <ol className="space-y-2">
        {sorted.map((q) => {
          const answered = q.correct + q.incorrect;
          const hard = isDifficult(q);
          return (
            <li key={q.questionId} className={`rounded-lg border p-3 ${hard ? 'border-destructive/40 bg-destructive/[0.04]' : 'border-border'}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <p className="min-w-0 flex-1 text-sm text-foreground">
                  <span className="mr-1.5 font-semibold text-muted-foreground">Q{q.order + 1}</span>
                  {q.text}
                </p>
                <div className="flex items-center gap-2">
                  {hard && (
                    <Badge variant="outline" className="gap-1 border-destructive/50 text-[10px] text-destructive">
                      <AlertTriangle className="h-3 w-3" aria-hidden /> Needs review
                    </Badge>
                  )}
                  <span className="text-sm font-semibold tabular-nums text-foreground">{formatPercent(q.accuracy)}</span>
                </div>
              </div>

              {/* Correct share of those who answered. Width is the figure beside it, so colour carries nothing alone. */}
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted" aria-hidden>
                <div className="h-full rounded-full bg-[var(--chart-1)]" style={{ width: `${answered > 0 ? (q.correct / answered) * 100 : 0}%` }} />
              </div>

              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span>
                  <span className="font-medium text-foreground">{q.correct}</span> correct
                </span>
                <span>
                  <span className="font-medium text-foreground">{q.incorrect}</span> incorrect
                </span>
                {q.unanswered > 0 && <span>{q.unanswered} not answered</span>}
                {q.correctIndex >= 0 && q.options[q.correctIndex] !== undefined && (
                  <span className="inline-flex items-center gap-1">
                    <Check className="h-3 w-3 text-primary" aria-hidden />
                    Answer: {letter(q.correctIndex)}. {q.options[q.correctIndex]}
                  </span>
                )}
                {q.commonWrongIndex !== null && q.options[q.commonWrongIndex] !== undefined && (
                  <span>
                    Most chosen wrong answer: {letter(q.commonWrongIndex)}. {q.options[q.commonWrongIndex]} (
                    {q.optionCounts[q.commonWrongIndex]})
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {coverage < totalResults && (
        <AnalyticsNote>
          Based on {coverage} of {totalResults} results. The others were recorded before answers were stored per question.
        </AnalyticsNote>
      )}
    </div>
  );
}
